import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    signal,
} from '@angular/core';
import {ConfigCardComponent} from '../config-card/config-card.component';
import {ConfigOverviewModel} from '../models/config-overview.model';
import {ProgressService} from '../../services/progress.service';
import {ConfigOverviewEmptyComponent} from '../config-overview-empty/config-overview-empty.component';
import {ConfigOverviewSkeletonComponent} from '../config-overview-skeleton/config-overview-skeleton.component';
import {ConfigDetailTableComponent} from '../config-table/config-detail-table.component';
import {ConfigCardModel} from '../../models/config-card.model';
import {OverviewToolbarComponent} from '../overview-toolbar/overview-toolbar.component';
import {ToolbarConfig} from '../overview-toolbar/overview-toolbar.models';
import {Paginator} from 'primeng/paginator';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
    selector: 'du-config-overview',
    imports: [
        ConfigCardComponent,
        ConfigOverviewEmptyComponent,
        ConfigOverviewSkeletonComponent,
        ConfigDetailTableComponent,
        OverviewToolbarComponent,
        Paginator,
        TranslatePipe,
    ],
    templateUrl: './config-overview.component.html',
    styleUrl: './config-overview.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfigOverviewComponent {
    cardDisabled = input(false);
    overviewModel = input<ConfigOverviewModel>();

    protected readonly _progressService = inject(ProgressService);

    readonly searchInput = signal('');
    readonly filterValues = signal<Record<string, unknown>>({});
    readonly viewType = signal<'grid' | 'list'>(
        localStorage.getItem('config-overview-view-type') === 'list' ? 'list' : 'grid',
    );

    readonly first = signal(0);
    readonly rows = signal(12);

    constructor() {
        effect(() => {
            this.searchInput();
            this.filterValues();
            this.first.set(0);
        });
    }

    readonly filteredCards = computed<ConfigCardModel[]>(() => {
        const model = this.overviewModel();
        if (!model) return [];

        const term = this.searchInput().trim().toLowerCase();
        const activeFilters = this.filterValues();
        const quickFilters = model.quickFilters ?? [];

        return model.cards.filter((card) => {
            if (term) {
                const matchesSearch = [card.title, card.searchName, card.id?.toString()]
                    .filter((v): v is string => typeof v === 'string' && v.length > 0)
                    .some((v) => v.toLowerCase().includes(term));
                if (!matchesSearch) return false;
            }
            for (const filter of quickFilters) {
                const value = activeFilters[filter.key];
                if (value != null && !filter.matcher(card, value)) return false;
            }
            return true;
        });
    });

    readonly paginatedCards = computed<ConfigCardModel[]>(() => {
        const cards = this.filteredCards();
        const start = this.first();
        const size = this.rows();
        return cards.slice(start, start + size);
    });

    onPageChange(event: any): void {
        this.first.set(event.first);
        this.rows.set(event.rows);
    }

    readonly toolbarConfig = computed<ToolbarConfig | null>(() => {
        const model = this.overviewModel();
        if (!model) return null;
        const [primary, secondary] = model.buttons ?? [];
        return {
            title: model.title,
            total: model.cards.length,
            filteredCount:
                this.filteredCards().length < model.cards.length ? this.filteredCards().length : undefined,
            primaryAction: primary?.text,
            primaryDisabled: primary?.disabled,
            secondaryAction: secondary?.text,
            secondaryDisabled: secondary?.disabled,
            quickFilters: model.quickFilters,
            filterValues: this.filterValues(),
        };
    });

    onPrimaryClick(): void {
        this.overviewModel()?.buttons?.[0]?.onClick();
    }

    onSecondaryClick(): void {
        this.overviewModel()?.buttons?.[1]?.onClick();
    }

    onFilterChange(event: { key: string; value: unknown }): void {
        this.filterValues.update((current) => ({...current, [event.key]: event.value}));
    }

    onFilterRemoved(key: string): void {
        this.filterValues.update((current) => {
            const next = {...current};
            delete next[key];
            return next;
        });
    }

    onFiltersCleared(): void {
        this.filterValues.set({});
    }

    toggleViewType(view: 'grid' | 'list'): void {
        this.viewType.set(view);
        localStorage.setItem('config-overview-view-type', view);
    }

    shouldDisableCard(card: ConfigCardModel): boolean {
        return this.cardDisabled() || !card.isActive(card.body?.inputs['appLicenseOverviewModel']);
    }
}
